import {
  WbAutocomplete,
  WbChip,
  WbComponentsListArray,
  customAlertApiRef,
  renderMessageWithSeverity,
  useTruncatedStyles,
} from '@agilelab/plugin-wb-platform';
import { CustomError } from '@agilelab/plugin-wb-platform-common';
import {
  WittyInputAdornment,
  WittyNoContextTooltip,
  useWittyAutoselect,
  useWittyTable,
  useWittyTableRow,
} from '@agilelab/plugin-wb-witty-react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Checkbox,
  FormControl,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { AutocompleteRenderOptionState } from '@material-ui/lab';
import {
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePrevious } from '../../hooks/useEventStream';
import { extractCustomProperties, isHidden } from '../../utils';
import {
  CustomUrlOperations,
  fetchAllMicroserviceValues,
  fetchMicroserviceValues,
  getApiConfigId,
  parseConfiguration,
  parseDynamicFields,
  validateUiSchema,
} from './CustomUrlUtilities';
import {
  AutocompleteOption,
  CustomUrlPickerProps,
  PickerMessage,
  retrievalErrorPickerMessage,
} from './schema';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

const ITEM_HEIGHT = 36;
const HEADER_HEIGHT = 60;
const MAX_HEIGHT = 300;

const useStyles = makeStyles(theme => ({
  ul: {
    maxHeight: `${MAX_HEIGHT}px`,
  },
  headerText: {
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const generateColumns = (fields: string[], options: Record<string, any>[]) => {
  const columns = fields.map(field => {
    const optionLengths = options.map(option => `${option[field]}`.length);
    const maxOptionLength = Math.max(...optionLengths);
    return fields.length === 1
      ? `minmax(${maxOptionLength * 10}px, 1fr)`
      : `max(${maxOptionLength * 10}px,${field.length * 10}px )`;
  });
  const stringColumns = columns.join(' ');
  const gridColumns = `40px ${stringColumns}`;
  return gridColumns;
};

const TableHeader: FC<{
  children?: ReactNode;
  fields?: string[];
  options?: Record<string, any>[];
}> = ({ children, fields = [], options = [] }) => {
  const classes = useStyles();

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridGap: '20px',
          gridTemplateColumns: generateColumns(fields, options),
          padding: '6px 12px',
          width: '100%',
        }}
      >
        <div />

        {fields.map(field => (
          <Typography
            key={field}
            style={{ padding: 0 }}
            className={classes.headerText}
          >
            {field}
          </Typography>
        ))}
      </div>

      {children}
    </>
  );
};

const TableRowOption: FC<{
  option: any;
  state: AutocompleteRenderOptionState;
  uiSchema: CustomUrlPickerProps['uiSchema'];
  fields?: string[];
  options?: Record<string, any>[];
}> = ({ option, state, fields = [], options = [] }) => {
  const truncatedClass = useTruncatedStyles({});

  const { selected } = state;

  return (
    <div
      style={{
        height: `${ITEM_HEIGHT}px`,
        display: 'grid',
        gridGap: '20px',
        gridTemplateColumns: generateColumns(fields, options),
        width: '100%',
      }}
    >
      <Checkbox style={{ padding: 0 }} checked={selected} />
      {fields
        .filter(key => Object.keys(option).includes(key))
        .map(key => (
          <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
            <div className={truncatedClass.truncated}>{option[key]}</div>
          </div>
        ))}
    </div>
  );
};

const TagsRenderer = ({
  options,
  getTagProps,
  selectedField,
}: {
  options: any[];
  getTagProps: any;
  selectedField?: string;
}) => {
  const elements = useMemo(() => {
    return options.map((option: any, index) => (
      <WbChip
        {...getTagProps({ index })}
        key={option.id}
        // remove classes added by MUI
        className={undefined}
        // remove margin for WbComponentsListArray to work correctly
        style={{ margin: 0 }}
        label={
          typeof option === 'object'
            ? `${option[selectedField ?? ''] ?? option}`
            : option
        }
      />
    ));
  }, [options, getTagProps, selectedField]);

  return (
    <WbComponentsListArray
      gap={8}
      renderContainer={(children, { totalWidth }) => (
        <div
          style={{
            minWidth: 50,
            overflow: 'hidden',
            flexGrow: 2,
            flexShrink: 1,
            // when we know the total size all the elements occupy, we set it as maxWidth,
            // there would be no point in making the container larger than that
            maxWidth: totalWidth !== null ? totalWidth + 5 : undefined,
          }}
        >
          {children}
        </div>
      )}
      children={elements}
    />
  );
};

/**
 * This Picker enables users to select multiple values fetched from a microservice.
 * @param props
 * @returns
 */
export const CustomUrlPicker = (props: CustomUrlPickerProps) => {
  const {
    onChange,
    schema: {
      title = 'Custom URL',
      description = 'A list of values fetched from a Microservice',
    },
    required,
    uiSchema,
    rawErrors,
    idSchema,
    formData,
    formContext,
  } = props;

  const alertApi = useApi(customAlertApiRef);
  const identityApi = useApi(identityApiRef);

  const [searchInput, setSearchInput] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>(
    [],
  );
  const autocompleteValues = useMemo(() => formData || [], [formData]);
  const theme = useTheme();
  const prevFormContext = usePrevious(formContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [enableSearch, setEnableSearch] = useState<boolean>(true);
  const [messages, setMessages] = useState<Map<string, PickerMessage>>(
    new Map([]),
  );
  const [templateParams, setTemplateParams] = useState<any>();

  const config = useApi(configApiRef);
  const maxNumberToSelect = uiSchema['ui:options']?.maxNumberToSelect;

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id);
  }, [idSchema]);

  const limitReached =
    !!maxNumberToSelect && autocompleteValues.length >= maxNumberToSelect;

  const [page, setPage] = useState<number>(0);
  const classes = useStyles();
  const listElem = useRef<HTMLDivElement>();
  const mounted = useRef<HTMLDivElement>();

  const lastVerticalScrollPosition = useRef<number>(0);
  const fetching = useRef<boolean>(false);

  const validatedUiSchema = useMemo(() => {
    try {
      return validateUiSchema(uiSchema);
    } catch (err) {
      alertApi.post({
        error: new CustomError(
          'Error: CustomUrlPicker Validation',
          err.message,
        ),
        severity: 'error',
      });
      return undefined;
    }
  }, [uiSchema, alertApi]);

  const microserviceConfig = useMemo(() => {
    if (!validatedUiSchema) return undefined;
    try {
      return parseConfiguration(
        config,
        validatedUiSchema,
        CustomUrlOperations.RETRIEVAL,
      );
    } catch (err) {
      alertApi.post({
        error: new CustomError(
          'Error: CustomUrlPicker Configuration',
          err.message,
        ),
        severity: 'error',
      });
      return undefined;
    }
  }, [config, validatedUiSchema, alertApi]);

  const resetCallback = useCallback(() => {
    // Notify dependency manager to propagate changes to dependents, or fallback to onChange
    if (dependencyManager?.notifyUpdate && fieldPath) {
      dependencyManager.notifyUpdate({
        fieldPath,
        value: undefined,
        source: 'user',
        intent: 'manual',
      });
    } else {
      onChange(undefined);
    }
    setMessages(prevErrors => {
      const currentErrors = new Map(prevErrors);
      currentErrors.delete('limit_error');
      return currentErrors;
    });
  }, [onChange, dependencyManager, fieldPath]);

  /**
   * This useEffect method is used to parse dynamic parameters and to set the errors list in case of missing resolution.
   * This is used separately from the others to make the CustomUrlPicker changes on the basis of the value of dependent fields.
   */
  useEffect(() => {
    if (validatedUiSchema)
      setTemplateParams(
        parseDynamicFields(
          formContext,
          prevFormContext,
          resetCallback,
          setEnableSearch,
          setPage,
          setMessages,
          validatedUiSchema['ui:apiSpec']!.retrieval.params,
        ),
      );
  }, [
    onChange,
    formContext,
    prevFormContext,
    setMessages,
    validatedUiSchema,
    resetCallback,
  ]);

  /** Calculate the height of the listbox based on the number of options and limit.
   * This is an estimated value that does not take into account padding and margin,
   * so as to make it smaller than the actual value and make the scrollbar appear
   */
  const listHeight = useMemo(() => {
    const itemCount = filteredOptions.length;

    const maxVisibleItems = templateParams?.limit || 5;

    if (itemCount === 0) return 'auto';

    const calculatedHeight = Math.min(
      itemCount * ITEM_HEIGHT + HEADER_HEIGHT,
      maxVisibleItems * ITEM_HEIGHT + HEADER_HEIGHT,
      MAX_HEIGHT,
    );
    return `${calculatedHeight}px`;
  }, [filteredOptions.length, templateParams?.limit]);

  /**
   * This useEffect hook implements the search mechanism by using debounce. When a user types some string
   * in the picker, useEffect is triggered and after a delay it performs a request to the microservice.
   */
  useEffect(() => {
    if (validatedUiSchema && enableSearch && microserviceConfig) {
      setLoading(true);
      const microserviceValues = setTimeout(
        () =>
          fetchMicroserviceValues(
            microserviceConfig,
            page,
            templateParams,
            searchInput,
            config,
            identityApi,
          )
            .then(response => {
              setLoading(false);
              setEnableSearch(false);
              if (response.ok) {
                setFilteredOptions(response.options);
              } else {
                setFilteredOptions([]);
                setMessages(prevErrors =>
                  prevErrors.set(
                    retrievalErrorPickerMessage.key,
                    retrievalErrorPickerMessage.value,
                  ),
                );
              }
            })
            .catch(err => {
              setLoading(false);
              setEnableSearch(false);
              alertApi.post({
                error: new CustomError(
                  'Error: CustomUrlPicker HTTP request',
                  err.message,
                ),
                severity: 'error',
              });
            }),
        1500,
      );
      return () => clearTimeout(microserviceValues);
    }
    return undefined;
  }, [
    enableSearch,
    validatedUiSchema,
    templateParams,
    searchInput,
    alertApi,
    microserviceConfig,
    page,
    config,
    identityApi,
  ]);

  // check if the selected option is beyond the limit and if it has already been selected
  const checkLimit = useCallback(
    (option: any) =>
      limitReached &&
      !autocompleteValues.map((val: any) => val.id).includes(option.id),
    [limitReached, autocompleteValues],
  );

  const selectFieldsToSave = useCallback(
    (val: Record<string, any> | string) => {
      return typeof val === 'object'
        ? Object.keys(val)
            .filter(key =>
              (
                validatedUiSchema?.['ui:fieldsToSave'] ??
                validatedUiSchema?.['ui:displayFields']
              )?.includes(key),
            )
            .reduce((acc, curr) => ({ ...acc, [curr]: val[curr] }), {})
        : val;
    },
    [validatedUiSchema],
  );

  const fieldId = useMemo(() => `${idSchema.$id}`, [idSchema.$id]);

  const wittyTable = useWittyTable();
  const wittyTableRow = useWittyTableRow();
  const canAutocomplete = wittyTableRow?.canAutocomplete;
  const triggeringColumns = wittyTable?.triggeringColumns ?? [];

  const processValues = useCallback(
    (val: any) => {
      if (required && Array.isArray(val) && !val.length) {
        setMessages(prevErrors => {
          prevErrors.delete('limit_error');
          return prevErrors;
        });
        return undefined;
      }

      let values = val;
      const limit = maxNumberToSelect;
      if (limit && val.length >= limit) {
        if (val.length > limit) {
          values = values.slice(0, limit);
        }
        setMessages(prevErrors =>
          prevErrors.set('limit_error', {
            line: 'You have reached the maximum number of items to select. Just delete and reselect one or more values, if you want to replace them.',
            severity: 'info',
          }),
        );
      } else {
        setMessages(prevErrors => {
          prevErrors.delete('limit_error');
          return prevErrors;
        });
      }
      return values.map(selectFieldsToSave);
    },
    [maxNumberToSelect, required, selectFieldsToSave],
  );

  const onSelect = useCallback(
    (_: any, val: any) => {
      const values = processValues(val);

      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: values,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(values);
      }
    },
    [processValues, onChange, dependencyManager, fieldPath],
  );

  /**
   * Fetches the entire options from the microservice, page by page, until a page with 0 elements is met (the last one)
   */
  const fetchAllOptions = useCallback(async () => {
    if (!microserviceConfig) throw new Error('Error in microservice config');
    return fetchAllMicroserviceValues(
      microserviceConfig,
      templateParams,
      config,
      identityApi,
    );
  }, [templateParams, microserviceConfig, config, identityApi]);

  // a string that uniquely identifies the combination of the params to send to the microservice (except limit and page) and the microservice id
  const apiConfigIdentifier = useMemo(() => {
    if (!uiSchema['ui:apiSpec']) return null;
    return getApiConfigId(
      uiSchema['ui:apiSpec'].retrieval.microserviceId ||
        `${uiSchema['ui:apiSpec'].retrieval.baseUrl}/${uiSchema['ui:apiSpec'].retrieval.path}` ||
        '',
      templateParams,
    );
  }, [templateParams, uiSchema]);

  const witty = useWittyAutoselect({
    fieldId: idSchema.$id,
    schema: props.schema,
    onSelect: onSelect,
    optionsSetId: apiConfigIdentifier,
  });

  const updateWittyFields = wittyTable?.updateWittyFields;

  useEffect(() => {
    if (
      updateWittyFields &&
      apiConfigIdentifier &&
      witty.enabled &&
      canAutocomplete &&
      microserviceConfig
    ) {
      updateWittyFields({
        type: 'ADD_FIELD',
        payload: {
          optionsSetId: apiConfigIdentifier,
          fieldId: fieldId,
          type: 'multiselect',
          fetchOptions: fetchAllOptions,
          transformBeforeFill: val => processValues(val),
        },
      });
    }

    return () => {
      updateWittyFields?.({ type: 'REMOVE_FIELD', payload: fieldId });
    };
  }, [
    fieldId,
    fetchAllOptions,
    onSelect,
    witty.enabled,
    updateWittyFields,
    selectFieldsToSave,
    props.formContext,
    canAutocomplete,
    processValues,
    idSchema.$id,
    apiConfigIdentifier,
    microserviceConfig,
  ]);

  const customProps = extractCustomProperties(uiSchema);

  return (
    <FormControl
      style={{
        display: isHidden(uiSchema) ? 'none' : undefined,
        paddingTop: '0px',
        paddingBottom: '0px',
      }}
      required={required}
      error={rawErrors?.length > 0}
    >
      <WbAutocomplete
        size="small"
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          paddingTop: '0px',
          paddingBottom: '0px',
        }}
        id={idSchema?.$id}
        options={filteredOptions || []}
        multiple
        getOptionDisabled={checkLimit}
        disabled={witty.loading || props.disabled}
        loading={loading}
        placeholder="Type to search"
        value={autocompleteValues}
        onChange={onSelect}
        ref={mounted}
        classes={{
          listbox: classes.ul,
        }}
        startAdornment={
          witty.enabled && (
            <WittyNoContextTooltip
              triggeringColumns={triggeringColumns}
              hide={canAutocomplete}
            >
              <span>
                <WittyInputAdornment
                  disabled={!canAutocomplete}
                  loading={witty.loading}
                  onClick={witty.suggestOptions}
                />
              </span>
            </WittyNoContextTooltip>
          )
        }
        ListboxProps={{
          onScroll: (event: SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            const scrollPosition =
              listboxNode.scrollTop + listboxNode.clientHeight;

            const currentScrollPosition = Math.round(
              listboxNode.scrollTop + listboxNode.clientHeight,
            );
            const endPosition = Math.round(listboxNode.scrollHeight);

            const scrolledDown =
              scrollPosition > lastVerticalScrollPosition.current;

            lastVerticalScrollPosition.current = scrollPosition;
            /**
             * This part here takes in consideration that the infinite scrolling mechanism can work differently
             * depending on the screen resolution. This means that the scrollbar position can be considered at "the end"
             * of the list box also if there are 2 pixels away from the end.
             */
            if (
              scrolledDown &&
              currentScrollPosition - endPosition <= 2 &&
              currentScrollPosition - endPosition >= 0 &&
              validatedUiSchema &&
              microserviceConfig &&
              !fetching.current
            ) {
              fetching.current = true;
              fetchMicroserviceValues(
                microserviceConfig,
                page + 1,
                templateParams,
                searchInput,
                config,
                identityApi,
              )
                .then(results => {
                  if (results.ok) {
                    setFilteredOptions([
                      ...filteredOptions,
                      ...results.options,
                    ]);
                    setPage(prevPage => prevPage + 1);
                  } else {
                    setMessages(prevErrors =>
                      prevErrors.set(
                        retrievalErrorPickerMessage.key,
                        retrievalErrorPickerMessage.value,
                      ),
                    );
                  }
                })
                .catch(err =>
                  alertApi.post({
                    error: new CustomError(
                      'Error: CustomUrlPicker HTTP request',
                      err.message,
                    ),
                    severity: 'error',
                  }),
                )
                .finally(() => {
                  fetching.current = false;
                });
            }
          },
          ref: listElem,
          style: {
            overflowX: 'auto',
            height: listHeight,
            overflowY: 'scroll',
          },
        }}
        inputValue={searchInput}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'input') {
            setFilteredOptions([]);
            setPage(0);
            setEnableSearch(true);
            setSearchInput(newInputValue);
          }
        }}
        disableCloseOnSelect
        freeSolo={uiSchema['ui:options']?.allowArbitraryValues ?? false}
        getOptionLabel={(option: any) =>
          Object.keys(option)
            .map(key => `${key}: ${option[key]}`)
            .join(' ')
        }
        getOptionSelected={(option: any, val: any) => {
          if (typeof option === 'object' && typeof val === 'object') {
            return option.id === val.id;
          }

          return option === val;
        }}
        groupBy={() => ''}
        renderGroup={params => (
          <TableHeader
            fields={validatedUiSchema?.['ui:displayFields']}
            options={filteredOptions}
          >
            {params.children}
          </TableHeader>
        )}
        renderOption={(option: any, state) => (
          <TableRowOption
            key={option.id}
            fields={validatedUiSchema?.['ui:displayFields']}
            state={state}
            uiSchema={uiSchema}
            option={option}
            options={filteredOptions}
          />
        )}
        renderTags={(value, getTagProps) => {
          return (
            <TagsRenderer
              options={value}
              getTagProps={getTagProps}
              selectedField={uiSchema['ui:options']?.selectedField}
            />
          );
        }}
        label={title}
        helperText={
          messages.size
            ? renderMessageWithSeverity(Array.from(messages.values()), theme)
            : description
        }
        required={required}
        {...customProps}
        InputProps={{
          style: {
            flexWrap: 'nowrap',
            ...customProps?.InputProps?.style,
          },
        }}
      />
    </FormControl>
  );
};
